import React from 'react';

import {
  ContainerBox,
  fields,
  Form,
  LoadingOverlay
} from 'iron-forge-react-modules';
import {
  Box,
  Button,
  Grid,
  styled,
  Typography
} from '@mui/material';
import {
  activityTemplates,
  appFormUtil,
  routeDirectory
} from 'modules';

import { Auth } from 'aws-amplify';
import { LockOutlined } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import authImage3x from 'assets/images/authImage/3x.png';



interface IFormState {
  email: string;
  password: string;
  // Used when changing password
  confirmPassword: string;
}

const TextFieldController = fields.TextField.genControllerComponent<IFormState>();

const ButtonContainerBox = styled(Box)(({ theme }) => ({
  gap: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch'
}));

const FormGrid = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '327px',
  marginLeft: 'auto',
  marginRight: 'auto'
});

const HeaderTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4)
}));

const AuthImage = styled('img')({
  width: '100%',
  objectFit: 'cover',
  maxHeight: '100vh'
});



export const LogInActivity: React.FC = () => {
  const [oldPassword, setOldPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();



  const form = useForm<IFormState>({
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: ''
    }
  });



  async function processSubmit(formState: IFormState) {
    const {
      email,
      password,
      confirmPassword
    } = appFormUtil.trimFormStateValues(formState);
    try {
      setLoading(true);
      if (oldPassword) {
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match.');

        } else {
          await resetPassword({
            email,
            oldPassword,
            newPassword: password
          });
        }

      } else {
        await logIn({ email, password });
      }

    } catch (err: unknown) {
      if (err instanceof Error) {
        if (err.message === 'NEW_PASSWORD_REQUIRED') {
          window.alert("Your password must be changed. Please reset your password");
          setOldPassword(formState.password);
          form.resetField('password');

        } else if (err.message) {
          alert(err?.message);
        }
      }

    } finally {
      setLoading(false);
    }
  }



  function handleClickForgotPassword() {
    navigate(routeDirectory.auth.forgotPassword.path());
  }



  return (
    <activityTemplates.BaseActivityTemplate.FC onlyAllowLoggedOutUsers>
      <LoadingOverlay.FC active={loading} />

      <Grid container>
        <Grid
          item
          xs={6}>
          <AuthImage src={authImage3x} />
        </Grid>

        <FormGrid item>
          <Form.FC onSubmit={form.handleSubmit(processSubmit)}>
            <ContainerBox.FC>
              <HeaderTypography variant="h2">
                {oldPassword ? "Reset Password" : "Sign In"}
              </HeaderTypography>

              {!oldPassword && (
                <TextFieldController
                  isRequired
                  formControl={form.control}
                  isDisabled={loading}
                  label="Email"
                  fieldKey="email"
                  type="email"
                  size="small" />
              )}

              <TextFieldController
                isRequired
                formControl={form.control}
                isDisabled={loading}
                label="Password"
                fieldKey="password"
                type="password"
                size="small"
                startAdornment={(
                  <LockOutlined />
                )} />

              {!!oldPassword && (
                <TextFieldController
                  isRequired
                  formControl={form.control}
                  isDisabled={loading}
                  label="Confirm Password"
                  fieldKey="confirmPassword"
                  type="password"
                  size="small"
                  startAdornment={(
                    <LockOutlined />
                  )} />
              )}

              <ContainerBox.FC>
                <ButtonContainerBox>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}>
                    {oldPassword ? "Change Password" : "Log In"}
                  </Button>

                  {!oldPassword && (
                    <Button
                      variant="contained"
                      disabled={loading}
                      onClick={handleClickForgotPassword}>
                      Forgotten Password?
                    </Button>
                  )}
                </ButtonContainerBox>
              </ContainerBox.FC>
            </ContainerBox.FC>
          </Form.FC>
        </FormGrid>
      </Grid>
    </activityTemplates.BaseActivityTemplate.FC>
  );
};



export const logIn = async ({
  email, password
}: {
  email: string;
  password: string;
}) => {
  try {
    const result = await Auth.signIn({
      username: email,
      password
    });

    if (result && result.challengeName === 'NEW_PASSWORD_REQUIRED') {
      throw new Error('NEW_PASSWORD_REQUIRED');
    }

  } catch (err) {
    if (err instanceof Error && err.message !== 'NEW_PASSWORD_REQUIRED') {
      window.alert(err);
    } else {
      throw err;
    }
  }
};



export const resetPassword = async ({
  email, oldPassword, newPassword
}: {
  email: string;
  oldPassword: string;
  newPassword: string;
}) => {
  const result = await Auth.signIn({
    username: email,
    password: oldPassword
  });
  await Auth.completeNewPassword(result, newPassword);
};
